import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, ActivityIndicator, FlatList, ListRenderItem } from 'react-native';
import { useAppSelector } from '@/store';
import { Bulletin } from '@/types/residents/bulletin';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import ResidentBulletinCard from '@/components/residents/bulletins/ResidentBulletinCard';
import { useToast } from '@/contexts/admin/ToastContext';
import { useResidentBulletins } from '@/hooks/common/useResidentBulletins';
import { EmptyState } from '@/components';
import { strings } from '@/constants/residents';
import { DesktopBulletinsScreenSkeleton } from './DesktopBulletinsScreenSkeleton';

export const DesktopBulletinsScreen: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const { showError } = useToast();
  const DESKTOP_COLORS = useDesktopColors();
  const styles = useMemo(() => buildStyles(DESKTOP_COLORS), [DESKTOP_COLORS]);
  const { items: bulletins, isLoading, isFetching, hasMore, loadNext } = useResidentBulletins({
    propertyId: user?.propertyId,
    onError: () => showError('Failed to load bulletins'),
    pageSize: 25,
    throttleMs: 500,
  });

  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});

  const handleToggleExpanded = useCallback((id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const renderItem: ListRenderItem<Bulletin> = useCallback(({ item }) => {
    return (
      <View style={styles.centerColumn}>
        <ResidentBulletinCard
          bulletin={item}
          isExpanded={!!expanded[item.id]}
          onToggleExpanded={handleToggleExpanded}
        />
      </View>
    );
  }, [expanded, handleToggleExpanded]);

  const keyExtractor = useCallback((item: Bulletin) => String(item.id), []);

  const contentContainerStyle = useMemo(() => styles.content, []);

  return (
    <View style={styles.root}>
      <FlatList
        data={bulletins}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
        contentContainerStyle={contentContainerStyle}
        ListEmptyComponent={isLoading ? (
          <DesktopBulletinsScreenSkeleton />
        ) : (
          <EmptyState
            title={strings.notifications.empty.title}
            description={strings.notifications.empty.description}
            iconName="notifications-outline"
          />
        )}
        ListFooterComponent={isFetching && !isLoading && bulletins.length > 0 ? (
          <ActivityIndicator style={styles.loader} size="large" color={DESKTOP_COLORS.TEXT_SECONDARY} />
        ) : null}
        onEndReachedThreshold={0.5}
        onEndReached={() => {
          if (!hasMore) return;
          loadNext();
        }}
        removeClippedSubviews={true}
        windowSize={7}
        initialNumToRender={5}
        maxToRenderPerBatch={7}
      />
    </View>
  );
};

type DesktopColors = ReturnType<typeof useDesktopColors>;

const buildStyles = (DESKTOP_COLORS: DesktopColors) => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  content: {
    padding: 16,
    flexGrow: 1,
  },
  centerColumn: {
    width: '100%',
    maxWidth: 840,
    alignSelf: 'center',
    gap: 20,
  },
  loader: {
    marginTop: 24,
  },
  itemSeparator: {
    height: 20,
  },
});

export default DesktopBulletinsScreen;


